import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Tag } from 'antd';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { timezonesApi } from '@app/api/Timezones';
import type { ModelTimezone } from '@app/api/Timezones';
import { FRONT_WARNING, SENDOUT_RECEIVERS_PATH } from '@app/constants';
import { frequencies } from '@app/features/SendoutCreationModal/components/SendMessageModal/data/frequency';
import {
  useAttachments,
  useMemoizeObject,
  useRightsToSendouts,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { usePageContext } from '@app/providers/PageProvider/usePageContext';
import { useGetSendoutDetailsQuery } from '@app/services/rtkQuery/Sendouts/api';
import { useAppDispatch } from '@app/store';
import { AttachmentModes } from '@app/types';

import { getScheduleRange } from '@pages/Sendouts/ViewSendouts/utils';

import { getStatusText } from '@pages/Sendouts/utils/getStatusText';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export const useViewSendout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const { setTitleData } = usePageContext();
  const { downloadFile, handleFile, getClientFileHandler } = useAttachments(
    AttachmentModes.Sendouts
  );
  const { isMobile } = useWindowSize();
  const [query] = useSearchParams();
  const sendoutId = query.get('id');

  const { activeBranchId } = useUserPrint();

  const { data } = useGetSendoutDetailsQuery(
    {
      sendoutId: +sendoutId!,
      ActiveBranchId: activeBranchId,
    },
    {
      skip: !sendoutId || !activeBranchId,
    }
  );

  const [timezones, setTimezones] = useState<ModelTimezone[]>([]);

  const {
    Sendout,
    Tags,
    Attachments,
    MoS,
    MoE,
    ThE,
    ThS,
    TuE,
    TuS,
    SaE,
    SaS,
    SuE,
    SuS,
    WeE,
    WeS,
    FrE,
    FrS,
    Timezone,
  } = data || {};

  const {
    Id,
    Name,
    CountReceivers,
    Message,
    Status,
    TimeCreated,
    TimeStart,
    Integration,
    Channel,
    ErrorMessage,
    SendoutDelaySec,
    TimeCompleted,
  } = Sendout || {};

  const memoAttachments = useMemoizeObject(Attachments);

  const { isAccessToReceiversRegistry } = useRightsToSendouts();

  const infoBlock = useMemo(
    () => [
      {
        label: 'ID:',
        value: Id,
        labelDataTestId: 'id-title',
        valueDataTestId: 'id-value',
      },
      {
        label: 'Название:',
        value: Name,
        labelDataTestId: 'name-title',
        valueDataTestId: 'name-sendout',
      },
      {
        label: 'Канал:',
        value: Channel,
        labelDataTestId: 'channel-title',
        valueDataTestId: 'channel-name',
      },
      {
        label: 'Интеграция:',
        value: Integration,
        labelDataTestId: 'integration-title',
        valueDataTestId: 'integration-name',
      },
      {
        label: 'Количество получателей:',
        value: CountReceivers,
        labelDataTestId: 'recipients-count-title',
        valueDataTestId: 'recipients-count',
      },
      {
        label: 'Присвоить теги после отправки:',
        value: (
          <Flex gap="middle" vertical>
            {Tags?.map((tag) => (
              <Tag
                key={tag.Id}
                color={tag.Color}
                icon={<FontAwesomeIcon icon={tag.IconId as IconProp} />}
              >
                {tag.Name}
              </Tag>
            ))}
          </Flex>
        ),
        labelDataTestId: 'assign-tags-title',
        valueDataTestId: 'tag-name',
      },
      {
        label: 'Периодичность:',
        value: SendoutDelaySec
          ? frequencies.find(({ value }) => value === SendoutDelaySec)?.label ||
            '-'
          : '-',
        labelDataTestId: 'delay-title',
        valueDataTestId: 'delay-value',
      },
      {
        label: 'Создана:',
        value:
          TimeCreated && moment(TimeCreated).isValid()
            ? moment(TimeCreated).format('DD.MM.YYYY HH:mm')
            : '-',
        labelDataTestId: 'created-title',
        valueDataTestId: 'created-date-time',
      },
      {
        label: 'Старт:',
        value:
          TimeStart && moment(TimeStart).isValid()
            ? moment(TimeStart).format('DD.MM.YYYY HH:mm')
            : '-',
        labelDataTestId: 'start-title',
        valueDataTestId: 'start-value',
      },
      ...(TimeCompleted
        ? [
            {
              label: getStatusText(Status, ErrorMessage),
              value: moment(TimeCompleted).format('DD.MM.YYYY HH:mm'),
              labelDataTestId: 'time-completed-title',
              valueDataTestId: 'time-completed-value',
            },
          ]
        : []),
      {
        label: 'Статус:',
        value: getStatusText(Status, ErrorMessage),
        labelDataTestId: 'status-title',
        valueDataTestId: 'status-value',
      },
    ],
    [
      Channel,
      CountReceivers,
      ErrorMessage,
      Id,
      Integration,
      Name,
      SendoutDelaySec,
      Status,
      Tags,
      TimeCompleted,
      TimeCreated,
      TimeStart,
    ]
  );

  const scheduleBlock = useMemo(
    () => [
      {
        label: 'Понедельник',
        value: getScheduleRange(MoS, MoE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Вторник',
        value: getScheduleRange(TuS, TuE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Среда',
        value: getScheduleRange(WeS, WeE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Четверг',
        value: getScheduleRange(ThS, ThE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Пятница',
        value: getScheduleRange(FrS, FrE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Суббота',
        value: getScheduleRange(SaS, SaE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
      {
        label: 'Воскресенье',
        value: getScheduleRange(SuS, SuE),
        labelDataTestId: 'day-of-week-title',
        valueDataTestId: 'sendout-schedule-time',
      },
    ],
    [FrE, FrS, MoE, MoS, SaE, SaS, SuE, SuS, ThE, ThS, TuE, TuS, WeE, WeS]
  );

  const navigateToReceiversRegistry = useCallback(() => {
    if (Sendout) {
      navigate(`${SENDOUT_RECEIVERS_PATH}?id=${Id}`);
    }
  }, [Id, Sendout, navigate]);

  const attachmentsHandler = useCallback(() => {
    if (!memoAttachments || !memoAttachments.length) return;

    memoAttachments.forEach(({ Id, Extension }) => {
      handleFile({ Id, Extension });
    });
  }, [handleFile, memoAttachments]);

  const getTimezonesHandler = useCallback(async () => {
    const request = dispatch(
      timezonesApi.endpoints.GetTimezones.initiate(undefined, {
        forceRefetch: true,
      })
    );

    try {
      setTimezones(await request.unwrap());
    } catch (error) {
      showNotification(error);
    } finally {
      request.unsubscribe?.();
    }
  }, [dispatch, showNotification]);

  const titleHandler = useCallback(() => {
    if (!data || !data?.Sendout) return;

    setTitleData(`Рассылка "${data.Sendout.Name}"`);

    return () => {
      setTitleData('');
    };
  }, [data, setTitleData]);

  const emptySendoutIdHandler = useCallback(() => {
    if (!sendoutId) {
      showNotification('Не передан идентификатор рассылки', FRONT_WARNING);
    }
  }, [sendoutId, showNotification]);

  return {
    emptySendoutIdHandler,
    titleHandler,
    getTimezonesHandler,
    attachmentsHandler,
    navigateToReceiversRegistry,
    scheduleBlock,
    infoBlock,
    isMobile,
    downloadFile,
    getClientFileHandler,
    timezones,
    Sendout,
    Timezone,
    Message,
    Attachments,
    isAccessToReceiversRegistry,
  };
};
